import { Component, EventEmitter, OnDestroy, OnInit, Output } from '@angular/core';
import { ConfirmationService, MessageService } from 'primeng/api';
import { Subscription } from 'rxjs';
import { finalize } from 'rxjs/operators';
import { TranslateService } from '@ngx-translate/core';
import { AppraisalRelationshipStatus } from '../settings/models/appraisal-relationship-status.enum';
import { AppraisalAssignmentRequestDto } from '../settings/models/appraisal-assignment-request.dto';
import { AppraisalSettingsService } from '../settings/services/appraisal-settings.service';

@Component({
  selector: 'appraisals-requests',
  templateUrl: './appraisals-requests.component.html',
  styleUrls: ['./appraisals-requests.component.scss'],
  standalone: false
})
export class AppraisalsRequestsComponent implements OnInit, OnDestroy {
  @Output() requestsUpdated = new EventEmitter<AppraisalAssignmentRequestDto[]>();

  requests: AppraisalAssignmentRequestDto[] = [];
  isLoading = false;
  respondingRequestId: number | null = null;
  rejectDialogVisible = false;
  rejectionReason = '';
  rejectionReasonTouched = false;
  requestToReject: AppraisalAssignmentRequestDto | null = null;

  private subscriptions: Subscription[] = [];

  constructor(
    private appraisalSettingsService: AppraisalSettingsService,
    private confirmationService: ConfirmationService,
    private messageService: MessageService,
    private translate: TranslateService
  ) {}

  ngOnInit(): void {
    this.loadRequests();
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach(sub => sub.unsubscribe());
  }

  loadRequests(): void {
    this.isLoading = true;
    this.subscriptions.push(
      this.appraisalSettingsService
        .getMyRequests()
        .pipe(
          finalize(() => {
            this.isLoading = false;
          })
        )
        .subscribe({
          next: requests => {
            this.requests = requests || [];
            this.requestsUpdated.emit(this.requests);
          },
          error: () => {
            this.requests = [];
            this.requestsUpdated.emit(this.requests);
          }
        })
    );
  }

  canRespond(request: AppraisalAssignmentRequestDto): boolean {
    return request.Status === AppraisalRelationshipStatus.Pending;
  }

  approveRequest(request: AppraisalAssignmentRequestDto): void {
    this.confirmationService.confirm({
      message: this.translate.instant('CONFIRM_APPROVE_APPRAISAL_REQUEST'),
      accept: () => this.submitDecision(request, true)
    });
  }

  /**
   * Opens and initializes rejection popup form
   * @param request Request to reject
   */
  rejectRequest(request: AppraisalAssignmentRequestDto): void {
    this.requestToReject = request;
    this.rejectionReason = '';
    this.rejectionReasonTouched = false;
    this.rejectDialogVisible = true;
  }

  /**
   * Closes dialog and clears the fields
   */
  closeRejectDialog(): void {
    this.rejectDialogVisible = false;
    this.requestToReject = null;
    this.rejectionReason = '';
    this.rejectionReasonTouched = false;
  }

  /**
   * Sends submit rejection decision for a request
   * @returns void
   */
  confirmRejectRequest(): void {
    this.rejectionReasonTouched = true;

    if (!this.requestToReject || !this.hasValidRejectionReason()) {
      return;
    }

    const request = this.requestToReject;
    const rejectionReason = this.rejectionReason.trim();

    this.closeRejectDialog();
    this.submitDecision(request, false, rejectionReason);
  }

  getStatusText(statusId: AppraisalRelationshipStatus): string {
    const statusMap: { [key: number]: string } = {
      [AppraisalRelationshipStatus.Pending]: 'PENDING',
      [AppraisalRelationshipStatus.Active]: 'ACTIVE',
      [AppraisalRelationshipStatus.Rejected]: 'REJECTED',
      [AppraisalRelationshipStatus.Revoked]: 'REVOKED'
    };

    return statusMap[statusId] || 'UNKNOWN';
  }

  /**
   * Submits a response to a request
   * @param request request to respond to
   * @param approve Is the request approved?
   * @param rejectionReason Reason for the request rejection
   */
  private submitDecision(
    request: AppraisalAssignmentRequestDto,
    approve: boolean,
    rejectionReason?: string
  ): void {
    this.respondingRequestId = request.Id;

    this.appraisalSettingsService
      .respondToMyRequest(request.Id, {
        Approve: approve,
        RejectionReason: approve ? null : rejectionReason?.trim() || null
      })
      .pipe(
        finalize(() => {
          this.respondingRequestId = null;
        })
      )
      .subscribe({
        next: () => {
          this.messageService.add({
            severity: 'success',
            summary: this.translate.instant(
              'APPRAISAL_REQUEST_DECISION_SUCCESS_MESSAGE'
            )
          });
          this.loadRequests();
        },
        error: () => {
          this.messageService.add({
            severity: 'error',
            summary: this.translate.instant(
              'APPRAISAL_REQUEST_DECISION_ERROR_MESSAGE'
            )
          });
        }
      });
  }

  /**
   * Validates if anything is written in the rejection reason text box
   * @returns bool: is anything written there?
   */
  private hasValidRejectionReason(): boolean {
    return this.rejectionReason.trim().length > 0;
  }
}
